/** @jsxImportSource theme-ui */
import { useState } from "react";
import { useThemeUI } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import has from "lodash/fp/has";

import {
  selectAllImageUrlsForProduct,
  selectSelectedProduct,
  selectUserChoiceValues,
} from "store/app";
import { setProductId } from "store/app/appSlice";
import {
  AppLayout,
  ProductsSelector,
  LoadingIndicator,
  Product,
} from "components";
import { ModalSelector } from "components/Modals";
import { useAddCss, usePreloadImages, useSetQueryString } from "hooks";
import { formatExternalLink } from "utils";

export default function App({ publicDirectoryUrl }) {
  const {
    theme: { isDesktop },
  } = useThemeUI();

  const dispatch = useDispatch();

  const dispatchSetProductId = (id) => {
    dispatch(setProductId(id));
  };

  const {
    id,
    restrictedModeEnabled,
    appLogo,
    appHeaderLogo,
    appDivider,
    title,
    selectProductTitle,
    closeAppText,
    closeAppLink,
    products = [],
    uiText,
    //
    position,
    top,
    left,
    right,
    containerTop,
    labelTop,
    labelLeft,
    customCardLayout,
  } = useSelector(get(["app", "formData"]));

  const selectedProduct = useSelector(selectSelectedProduct);
  const modals = useSelector(getOr([], ["app", "modals"]));
  const userChoiceValues = useSelector(selectUserChoiceValues);

  const isDisplayingModals = modals.length > 0;

  useSetQueryString({
    family: (products.length > 1 && selectedProduct?.id) || null,
  });

  const selectedProductRequiresSizeChoice = Boolean(selectedProduct?.sizes);
  const sizesFieldName = get(["sizes", 0, "fields", 0, "name"])(
    selectedProduct
  );
  const selectedProductHasRequiredSizeValue =
    has(sizesFieldName)(userChoiceValues);

  const [renderSelectedProduct, setRenderSelectedProduct] = useState(
    Boolean(selectedProduct) &&
      (!selectedProductRequiresSizeChoice ||
        selectedProductHasRequiredSizeValue)
  );

  const renderProductSelector =
    products.length > 0 &&
    (!selectedProduct ||
      (selectedProductRequiresSizeChoice &&
        !selectedProductHasRequiredSizeValue));

  useAddCss({
    href: process.env.REACT_APP_TYPEKIT_HREF,
  });

  const productLogo = selectedProduct?.category?.logo;
  const variantLogo = selectedProduct?.category?.variantLogo;

  const allImageUrlsForProduct = useSelector(
    selectAllImageUrlsForProduct({ productId: selectedProduct?.id })
  );

  const preloadImages = selectedProduct?.preloadImages || false;

  const { isLoading: _isLoadingImages } = usePreloadImages({
    imageUrls: preloadImages ? allImageUrlsForProduct : [],
  });

  const isLoadingImages = (preloadImages === true && _isLoadingImages) || false;

  return (
    <AppLayout
      hasLoadedSelectedProduct={renderSelectedProduct && !isLoadingImages}
      appLogo={(isDesktop || !renderSelectedProduct) && appLogo}
      appTitle={isDesktop && !productLogo && title}
      productLogo={isDesktop && productLogo}
      variantLogo={isDesktop && variantLogo}
      closeAppText={closeAppText}
      closeAppLink={formatExternalLink(closeAppLink)}
      isDisplayingModals={isDisplayingModals}
      data-cy={id}
    >
      {isLoadingImages && (
        <LoadingIndicator
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}

      <ModalSelector
        product={selectedProduct}
        appLogo={appLogo}
        modals={modals}
      />

      {!renderSelectedProduct && (
        <AnimatePresence>
          {renderProductSelector && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              sx={{ width: "100%", height: "100%" }}
              onAnimationComplete={(definition) => {
                if (definition.opacity === 0) {
                  setRenderSelectedProduct(true);
                }
              }}
            >
              <ProductsSelector
                title={selectProductTitle}
                appDivider={appDivider}
                uiText={uiText}
                appLogo={appLogo}
                appHeaderLogo={appHeaderLogo}
                products={products}
                selectedProduct={selectedProduct}
                selectProduct={dispatchSetProductId}
                //
                position={position}
                top={top}
                left={left}
                right={right}
                containerTop={containerTop}
                labelTop={labelTop}
                labelLeft={labelLeft}
                customCardLayout={customCardLayout}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {!isLoadingImages && renderSelectedProduct && (
        <Product
          restrictedModeEnabled={restrictedModeEnabled}
          changeProduct={() => {
            setRenderSelectedProduct(false);
            dispatchSetProductId("");
          }}
          appHeaderLogo={appHeaderLogo}
          appDivider={appDivider}
          publicDirectoryUrl={publicDirectoryUrl}
        />
      )}
    </AppLayout>
  );
}
